import { FC, useCallback, useMemo } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  CircularProgress,
  TextField,
} from "@mui/material";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import __ from "src/utils/Translations";
import { IAbsenceTypeForm } from "src/Pages/Company/Absences/AbsenceType/AbsenceType.models";
import { useFetchAbsenceGroups } from "src/Pages/Company/Absences/api/useAbsenceGroups";
import Fieldset from "src/Pages/Company/Absences/Forms/Fieldset";

interface ISelectAbsenceGroupProps {
  disabled: boolean;
}

const SelectAbsenceGroup: FC<ISelectAbsenceGroupProps> = ({ disabled }) => {
  const companyId = useCompanyId();
  const { control } = useFormContext<IAbsenceTypeForm>();

  const presenceType = useWatch({
    control,
    name: "presenceType",
  });

  const { data: absenceGroups, isFetching } = useFetchAbsenceGroups(companyId);

  const absenceGroupOptions = useMemo(
    () =>
      absenceGroups
        .filter((o) => o.active && o.presenceType === presenceType)
        .map((o) => ({
          value: o.id,
          label: o.name,
        })) || [],
    [absenceGroups, presenceType]
  );

  const selectedAbsenceGroupId = useCallback(
    (value: string) =>
      absenceGroupOptions.find((o) => o.value === value) || null,
    [absenceGroupOptions]
  );

  return (
    <Fieldset
      legend={__("Grupa szablonu")}
      helpText={__(
        "Grupy porządkują wnioski. Możesz je dodać lub edytować w zakładce „Grupy nieobecności i obecności”."
      )}
    >
      <Controller
        name="absenceGroupId"
        control={control}
        render={({
          field: { value, onChange, ref },
          fieldState: { invalid, error },
        }) => (
          <Autocomplete
            fullWidth
            disablePortal
            options={absenceGroupOptions || []}
            value={selectedAbsenceGroupId(value)}
            loading={isFetching}
            onChange={(_event, newValue) => onChange(newValue?.value)}
            disabled={disabled}
            renderInput={(params: AutocompleteRenderInputParams) => (
              <TextField
                {...params}
                label={__("Przypisz grupę")}
                required
                error={invalid}
                helperText={error?.message}
                inputRef={ref}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isFetching ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                }}
                data-testid="absence-type-form-field__absence-group"
              />
            )}
          />
        )}
      />
    </Fieldset>
  );
};

export default SelectAbsenceGroup;
